import { useMemo } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import i18n from '../i18n';
import { api } from '../services/api';
import { toast } from '../store/toastStore';
import { apiErrorMessage } from '../components/sales/shared';
import { undoableDelete } from './undoableActions';
import { usePendingTicketStatus } from './ticketStatusPending';
import { getStatusLabel, type TicketStatus } from '../types';

/**
 * P-29 (QX-107, D-166 s.11) - a status change of a ticket / task with "בטל".
 *
 * The change itself leaves for the server only after the 6 s window, so the
 * customer notification, the completion event and the timer stops all happen
 * then; "בטל" before that = nothing happened (no revert request). It rides the
 * ONE P-27 scheduler (`undoableDelete`): the toast, the keepalive request on
 * pagehide / hidden tab (T-44) and the failure handling are shared, not copied.
 * The page shows the new status at once through `useShownStatus`.
 */
export const TICKET_STATUS_KIND = 'ticket_status';

/** The status to show: the one waiting in its undo window, else the real one. */
export function useShownStatus(id: string | undefined, status: string): string {
  const waiting = usePendingTicketStatus((s) => (id ? s.pending[id]?.status : undefined));
  return waiting ?? status;
}

/** Same as `useShownStatus` for a list: a row waiting for its status is shown with it. */
export function useWithPendingStatus<T extends { id: string; status: string }>(rows: T[]): T[] {
  const pending = usePendingTicketStatus((s) => s.pending);
  return useMemo(() => {
    if (Object.keys(pending).length === 0) return rows;
    return rows.map((r) => (pending[r.id] ? { ...r, status: pending[r.id].status } : r));
  }, [rows, pending]);
}

const KEYS = ['tickets', 'ticket', 'ticket-timeline', 'all-tasks', 'project', 'milestone', 'milestones', 'tasks', 'task', 'home', 'workspace', 'dashboard-daily-status'];
let tokenSeq = 0;

export function undoableTicketStatus(o: {
  qc: QueryClient;
  id: string;
  status: string;
  cascade?: boolean;
  /** DQ-087: the row's task_type - a request ('ticket') reads its own status names (D-146: "הושלמה", "בטיפול"). */
  taskType?: string | null;
  /** Runs once the change reached the server (and the lists were refreshed). */
  onCommitted?: () => void;
}) {
  const token = ++tokenSeq;
  const store = usePendingTicketStatus.getState();
  store.set(o.id, o.status, token);
  const refresh = () => Promise.all(KEYS.map((k) => o.qc.invalidateQueries({ queryKey: [k] })));
  const label = o.taskType !== undefined
    ? getStatusLabel(o.status as TicketStatus, (k) => i18n.t(k), o.taskType)
    : i18n.t(`status.${o.status}`, { defaultValue: o.status });
  return undoableDelete({
    kind: TICKET_STATUS_KIND,
    id: o.id,
    message: i18n.t('undoToast.statusChanged', { status: label }),
    run: () => api.updateTicketStatus(o.id, o.status, o.cascade),
    unload: {
      method: 'PATCH',
      path: `/tickets/${o.id}/status`,
      body: { status: o.status, ...(o.cascade !== undefined && { cascade_close_subtasks: o.cascade }) },
    },
    onCommitted: () => {
      // keep showing the new status until the refreshed data arrives
      void refresh().finally(() => { usePendingTicketStatus.getState().clear(o.id, token); o.onCommitted?.(); });
    },
    onRestore: () => { usePendingTicketStatus.getState().clear(o.id, token); },
    onError: (err) => {
      usePendingTicketStatus.getState().clear(o.id, token);
      toast('error', apiErrorMessage(err, i18n.t('ticketDetail.statusChangeFailed')));
    },
  });
}
